/**
 * Announcements Store (Zustand)
 *
 * Centralized store for system-wide announcements.
 * The Firestore listener is set up ONCE in AuthenticatedLayout via useAnnouncementsListener,
 * and all components read from this store via useAnnouncementsData.
 *
 * This eliminates redundant Firestore subscriptions that were occurring
 * when multiple components called useAnnouncements independently.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { logger } from '../utils/logger';

/** Announcement type determines display behavior */
export type AnnouncementType = 'urgent' | 'informational' | 'rich';

/** Display mode for rich announcements */
export type DisplayMode = 'auto-popup' | 'header-only';

/** Image attachment for rich announcements */
export interface AnnouncementImage {
  url: string;
  storagePath: string;
  uploadedAt: string;
}

/** Announcement document structure */
export interface Announcement {
  id: string;
  title: string;
  message: string;
  createdAt: Date;
  createdBy: string;
  active: boolean;
  type: AnnouncementType;
  /** Display mode for rich announcements (auto-popup shows as popup, header-only is passive) */
  displayMode?: DisplayMode;
  /** Sanitized HTML content for rich announcements */
  htmlContent?: string;
  /** Lexical EditorState JSON for rich announcements (primary storage) */
  contentJSON?: string;
  /** Uploaded images for rich announcements */
  images?: AnnouncementImage[];
  /** Scheduled activation time - announcement hidden until this time passes */
  scheduledAt?: Date | null;
}

/** LocalStorage key for dismissed announcements */
const DISMISSED_STORAGE_KEY = 'dismissed_announcements';

/** How long announcements show as popups (3 days in milliseconds) */
const POPUP_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Get dismissed announcement IDs from localStorage
 */
function getDismissedIds(): Set<string> {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (e) {
    logger.warn('[Announcements] Failed to parse dismissed IDs from localStorage');
  }
  return new Set();
}

/**
 * Save dismissed announcement IDs to localStorage
 */
function saveDismissedIds(ids: Set<string>): void {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...ids]));
  } catch (e) {
    logger.warn('[Announcements] Failed to save dismissed IDs to localStorage');
  }
}

interface AnnouncementsState {
  // Raw announcements from Firestore
  announcements: Announcement[];

  // Loading and error state
  isLoading: boolean;
  error: string | null;

  // Dismissed announcement IDs (persisted in localStorage)
  dismissedIds: Set<string>;

  // Actions - called by useAnnouncementsListener
  setAnnouncements: (announcements: Announcement[]) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - called by components
  dismissAll: () => void;
  markAsRead: (id: string) => void;
  isRead: (id: string) => boolean;

  // Computed getters
  getAllAnnouncements: () => Announcement[];
  getPopupAnnouncements: () => Announcement[];
  getIndexPageAnnouncements: () => Announcement[];
  getUnreadCount: () => number;
  getUnreadIndexCount: () => number;
}

export const useAnnouncementsStore = create<AnnouncementsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      announcements: [],
      isLoading: true,
      error: null,
      dismissedIds: getDismissedIds(),

      // Actions - called by useAnnouncementsListener
      setAnnouncements: (announcements) => {
        set({ announcements });

        // Clean up stale dismissed IDs
        const state = get();
        const announcementIds = new Set(announcements.map((a) => a.id));
        const validDismissedIds = new Set(
          [...state.dismissedIds].filter((id) => announcementIds.has(id))
        );

        if (validDismissedIds.size < state.dismissedIds.size) {
          set({ dismissedIds: validDismissedIds });
          saveDismissedIds(validDismissedIds);
          logger.log('[Announcements] Cleaned up stale dismissed IDs');
        }
      },

      setIsLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => {
        set({ error });
        if (error) {
          logger.warn('[Announcements] Error loading announcements:', error);
        }
      },

      // Actions - called by components
      dismissAll: () => {
        const state = get();
        const popupAnnouncements = state.getPopupAnnouncements();
        const newDismissedIds = new Set(state.dismissedIds);

        popupAnnouncements.forEach((announcement) => {
          newDismissedIds.add(announcement.id);
        });

        set({ dismissedIds: newDismissedIds });
        saveDismissedIds(newDismissedIds);

        logger.log(`[Announcements] Dismissed ${popupAnnouncements.length} announcements`);
      },

      markAsRead: (id) => {
        const state = get();
        if (state.dismissedIds.has(id)) return;

        const newDismissedIds = new Set(state.dismissedIds);
        newDismissedIds.add(id);
        set({ dismissedIds: newDismissedIds });
        saveDismissedIds(newDismissedIds);

        logger.log(`[Announcements] Marked announcement ${id} as read`);
      },

      isRead: (id) => get().dismissedIds.has(id),

      // Computed getters
      getAllAnnouncements: () => {
        const { announcements } = get();
        const now = Date.now();
        return announcements.filter((a) => {
          // Must be active
          if (!a.active) return false;
          // If scheduled for the future, hide it
          if (a.scheduledAt && a.scheduledAt.getTime() > now) return false;
          return true;
        });
      },

      getPopupAnnouncements: () => {
        const state = get();
        const allAnnouncements = state.getAllAnnouncements();
        const now = Date.now();
        const cutoffTime = now - POPUP_WINDOW_MS;

        return allAnnouncements.filter((announcement) => {
          // Determine if this announcement should popup
          const shouldPopup =
            announcement.type === 'urgent' ||
            (announcement.type === 'rich' && announcement.displayMode !== 'header-only');

          if (!shouldPopup) return false;

          // Must be within popup window (last 3 days)
          const announcementTime = announcement.createdAt.getTime();
          if (announcementTime < cutoffTime) return false;

          // Must not be dismissed
          if (state.dismissedIds.has(announcement.id)) return false;

          return true;
        });
      },

      getIndexPageAnnouncements: () => get().getAllAnnouncements(),

      getUnreadCount: () => get().getPopupAnnouncements().length,

      getUnreadIndexCount: () => {
        const state = get();
        const allAnnouncements = state.getAllAnnouncements();
        return allAnnouncements.filter((a) => !state.dismissedIds.has(a.id)).length;
      },
    }),
    { name: 'AnnouncementsStore' }
  )
);
